<!-- ══════════════════════════════════════════════════════════════
     AI Clinical Copilot Panel
     Analysis runs only when the doctor clicks Analyze (not while typing)
     ══════════════════════════════════════════════════════════════ -->

<ng-container *ngIf="aiEnabled">

<!-- Idle / ready state -->
<div class="cca-idle" *ngIf="!showPanel && !loading">
  <div
    class="cca-hint"
    *ngIf="symptoms.trim().length > 0 && symptoms.trim().length < minCharacters"
  >
    <i class="pi pi-sparkles"></i>
    Enter at least {{ minCharacters }} characters — AI runs automatically when loaded or when you leave the field.
  </div>

  <div class="cca-idle__ready" *ngIf="canAnalyze && !suggestions && !loading">
    <p class="cca-idle__text">
      <i class="pi pi-sparkles"></i>
      Tab out of chief complaints to analyze, or click below.
    </p>
    <button
      type="button"
      class="cca-analyze-btn"
      (click)="analyze()"
    >
      <i class="pi pi-sparkles"></i>
      Analyze now
    </button>
  </div>
</div>

<div class="cca-panel" *ngIf="showPanel || loading">

  <!-- ── Header ── -->
  <div class="cca-header">
    <div class="cca-header__left">
      <span class="cca-header__icon">
        <i class="pi pi-sparkles"></i>
      </span>
      <div>
        <div class="cca-header__title">AI Clinical Assistant</div>
        <div class="cca-header__sub">Decision support only — not a final diagnosis</div>
      </div>
    </div>

    <!-- Completeness indicator (shown once suggestions exist) -->
    <div class="cca-completeness" *ngIf="suggestions && !loading">
      <div class="cca-completeness__label">
        Clinical Completeness
        <span class="cca-completeness__pct">{{ completenessScore }}%</span>
      </div>
      <div class="cca-completeness__track">
        <div
          class="cca-completeness__bar"
          [ngClass]="completenessClass"
          [style.width.%]="completenessScore"
        ></div>
      </div>
      <div class="cca-completeness__status">{{ completenessLabel }}</div>
    </div>

    <button
      type="button"
      class="cca-refresh-btn"
      *ngIf="!loading && (isStale || suggestions)"
      [disabled]="!canAnalyze || loading"
      (click)="analyze(true)"
      [title]="isStale ? 'Chief complaints changed — refresh analysis' : 'Run analysis again'"
    >
      <i class="pi pi-refresh"></i>
      {{ isStale ? 'Refresh' : 'Re-analyze' }}
    </button>
  </div>

  <div class="cca-stale-banner" *ngIf="!loading && isStale">
    <i class="pi pi-info-circle"></i>
    Chief complaints changed since last analysis. Click Refresh for updated suggestions.
  </div>

  <!-- ── Loading state ── -->
  <div class="cca-loading" *ngIf="loading">
    <span class="cca-loading__spinner"></span>
    <span>Analyzing symptoms…</span>
  </div>

  <!-- ── Error state ── -->
  <div class="cca-error" *ngIf="!loading && errorMessage">
    <i class="pi pi-exclamation-circle"></i>
    {{ errorMessage }}
  </div>

  <ng-container *ngIf="!loading && suggestions as result">

    <!-- ══════════════════════════════════════════════════════
         STATUS = insufficient_information
         Show: Ask Next → Missing Info → (no diagnoses yet)
         ══════════════════════════════════════════════════════ -->
    <ng-container *ngIf="result.status === 'insufficient_information'">

      <!-- Status banner -->
      <div class="cca-status-banner cca-status-banner--info">
        <i class="pi pi-info-circle"></i>
        More clinical details needed before differential analysis can begin.
      </div>

      <!-- Ask Next (shown first when data is insufficient) -->
      <section class="cca-section" *ngIf="result.suggested_questions?.length">
        <div class="cca-section__header">
          <i class="pi pi-question-circle"></i>
          <span class="cca-section__title">Ask Next</span>
          <span class="cca-section__count">{{ result.suggested_questions.length }}</span>
        </div>
        <div class="cca-question-grid">
          <button
            type="button"
            class="cca-question-chip"
            [class.cca-question-chip--added]="addedNotes.has(q)"
            *ngFor="let q of result.suggested_questions"
            (click)="onAddNote(q)"
            [title]="addedNotes.has(q) ? 'Already added' : 'Add to notes'"
          >
            <span class="cca-question-chip__text">{{ q }}</span>
            <span class="cca-question-chip__action">
              <i class="pi" [ngClass]="addedNotes.has(q) ? 'pi-check' : 'pi-plus'"></i>
            </span>
          </button>
        </div>
      </section>

      <!-- Missing Information (collapsed by default) -->
      <section class="cca-section" *ngIf="result.missing_information?.length">
        <button
          type="button"
          class="cca-accordion-toggle"
          (click)="missingExpanded = !missingExpanded"
        >
          <span class="cca-accordion-toggle__left">
            <i class="pi pi-clipboard"></i>
            Missing Information
            <span class="cca-section__count">{{ result.missing_information.length }}</span>
          </span>
          <i class="pi cca-accordion-toggle__chevron"
             [ngClass]="missingExpanded ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
        </button>
        <ul class="cca-list cca-list--muted" *ngIf="missingExpanded">
          <li *ngFor="let item of result.missing_information">{{ item }}</li>
        </ul>
      </section>

    </ng-container>

    <!-- ══════════════════════════════════════════════════════
         STATUS = analysis_available
         Show: Diagnoses → Ask Next → Watch For → Missing
         ══════════════════════════════════════════════════════ -->
    <ng-container *ngIf="result.status === 'analysis_available'">

      <!-- Status banner -->
      <div class="cca-status-banner cca-status-banner--ready">
        <i class="pi pi-check-circle"></i>
        Sufficient information available — preliminary analysis ready.
      </div>

      <!-- 1. Likely Possibilities -->
      <section class="cca-section" *ngIf="hasDiagnoses">
        <div class="cca-section__header">
          <i class="pi pi-list"></i>
          <span class="cca-section__title">Likely Possibilities</span>
          <span class="cca-section__count">{{ topDiagnoses.length }}</span>
        </div>

        <div class="cca-dx-stack">
          <div
            class="cca-dx-card"
            *ngFor="let dx of topDiagnoses; let i = index"
          >
            <div class="cca-dx-card__top">
              <!-- Rank -->
              <span class="cca-dx-card__rank">{{ i + 1 }}</span>

              <!-- Name + badge -->
              <div class="cca-dx-card__info">
                <span class="cca-dx-card__name">{{ dx.name }}</span>
                <span [ngClass]="getConfidenceClass(dx.confidence)">
                  {{ dx.probability_percentage ? dx.probability_percentage + '% ' : '' }}{{ dx.confidence }}
                </span>
              </div>

              <!-- Why? tooltip toggle -->
              <button
                type="button"
                class="cca-why-btn"
                (click)="toggleTooltip(dx.name)"
                [class.cca-why-btn--active]="openTooltip === dx.name"
                title="Show reasoning"
              >
                Why?
              </button>

              <!-- Add to Diagnosis -->
              <button
                type="button"
                class="cca-action-btn"
                [class.cca-action-btn--done]="addedDiagnoses.has(dx.name)"
                [disabled]="addedDiagnoses.has(dx.name)"
                (click)="onAddDiagnosis(dx.name)"
              >
                <i class="pi" [ngClass]="addedDiagnoses.has(dx.name) ? 'pi-check' : 'pi-plus'"></i>
                {{ addedDiagnoses.has(dx.name) ? 'Added' : 'Add to Diagnosis' }}
              </button>
            </div>

            <!-- Reasoning tooltip (expands inline) -->
            <div class="cca-dx-card__reason" *ngIf="openTooltip === dx.name">
              <i class="pi pi-lightbulb"></i>
              {{ dx.reason }}
            </div>
          </div>
        </div>
      </section>

      <!-- 2. Differentiating Questions -->
      <section class="cca-section" *ngIf="result.suggested_questions?.length">
        <button
          type="button"
          class="cca-accordion-toggle"
          (click)="questionsExpanded = !questionsExpanded"
        >
          <span class="cca-accordion-toggle__left">
            <i class="pi pi-question-circle"></i>
            Questions to Narrow Diagnosis
            <span class="cca-section__count">{{ result.suggested_questions.length }}</span>
          </span>
          <i class="pi cca-accordion-toggle__chevron"
             [ngClass]="questionsExpanded ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
        </button>
        <div class="cca-question-grid" *ngIf="questionsExpanded" style="margin-top: 12px;">
          <button
            type="button"
            class="cca-question-chip"
            [class.cca-question-chip--added]="addedNotes.has(q)"
            *ngFor="let q of result.suggested_questions"
            (click)="onAddNote(q)"
            [title]="addedNotes.has(q) ? 'Already added to notes' : 'Add to chief complaints'"
          >
            <span class="cca-question-chip__text">{{ q }}</span>
            <span class="cca-question-chip__action">
              <i class="pi" [ngClass]="addedNotes.has(q) ? 'pi-check' : 'pi-plus'"></i>
            </span>
          </button>
        </div>
      </section>

      <!-- 3. Watch For (only when analysis_available + red flags exist) -->
      <section class="cca-section cca-section--danger" *ngIf="hasRedFlags">
        <div class="cca-section__header cca-section__header--danger">
          <i class="pi pi-exclamation-triangle"></i>
          <span class="cca-section__title">Watch For</span>
        </div>
        <ul class="cca-list cca-list--danger">
          <li *ngFor="let flag of result.red_flags">{{ flag }}</li>
        </ul>
      </section>

      <!-- 4. Missing Information (collapsed) -->
      <section class="cca-section" *ngIf="result.missing_information?.length">
        <button
          type="button"
          class="cca-accordion-toggle"
          (click)="missingExpanded = !missingExpanded"
        >
          <span class="cca-accordion-toggle__left">
            <i class="pi pi-clipboard"></i>
            Missing Information
            <span class="cca-section__count">{{ result.missing_information.length }}</span>
          </span>
          <i class="pi cca-accordion-toggle__chevron"
             [ngClass]="missingExpanded ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
        </button>
        <ul class="cca-list cca-list--muted" *ngIf="missingExpanded">
          <li *ngFor="let item of result.missing_information">{{ item }}</li>
        </ul>
      </section>

      <!-- 5. Suggested Reports -->
      <section class="cca-section" *ngIf="result.suggested_reports?.length">
        <div class="cca-section__header">
          <i class="pi pi-file-edit"></i>
          <span class="cca-section__title">Suggested Reports</span>
          <span class="cca-section__count">{{ result.suggested_reports.length }}</span>
        </div>
        <div class="cca-report-grid">
          <div
            class="cca-report-card"
            *ngFor="let rep of result.suggested_reports"
          >
            <div class="cca-report-card__name">{{ rep.name }}</div>
            <div class="cca-report-card__reason">{{ rep.reason }}</div>
            <button
              type="button"
              class="cca-action-btn cca-action-btn--report"
              [class.cca-action-btn--done]="addedReports.has(rep.name)"
              [disabled]="addedReports.has(rep.name)"
              (click)="onAddReport(rep.name)"
            >
              <i class="pi" [ngClass]="addedReports.has(rep.name) ? 'pi-check' : 'pi-plus'"></i>
              {{ addedReports.has(rep.name) ? 'Added' : 'Order' }}
            </button>
          </div>
        </div>
      </section>

    </ng-container>

  </ng-container>
</div>

</ng-container>
